import { z } from 'zod';

const READINESS_MAX_AGE_MS = 15_000;

const ReadinessComponentsSchema = z
    .object({
        control: z.boolean().nullable().optional(),
        video: z.boolean().nullable().optional(),
        hardware: z.boolean().nullable().optional(),
        livekit_connection_state: z.string().nullable().optional(),
        published_at: z.string().nullable().optional(),
    })
    .passthrough();

export type ReadinessComponents = {
    control: boolean | null;
    video: boolean | null;
    hardware: boolean | null;
    livekitConnectionState: string | null;
    publishedAt: string | null;
};

const EMPTY_READINESS_COMPONENTS: ReadinessComponents = {
    control: null,
    video: null,
    hardware: null,
    livekitConnectionState: null,
    publishedAt: null,
};

const parseTimestamp = (value: string | null): number | null => {
    if (!value) {
        return null;
    }
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
};

const isReadinessFresh = (publishedAt: string | null): boolean => {
    const timestamp = parseTimestamp(publishedAt);
    if (timestamp === null) {
        return false;
    }
    const ageMs = Date.now() - timestamp;
    return ageMs >= -READINESS_MAX_AGE_MS && ageMs <= READINESS_MAX_AGE_MS;
};

const staleReadinessComponents = (publishedAt: string | null): ReadinessComponents => ({
    control: false,
    video: false,
    hardware: false,
    livekitConnectionState: 'disconnected',
    publishedAt,
});

export const coerceReadinessComponents = (value: unknown): ReadinessComponents => {
    const parsed = ReadinessComponentsSchema.safeParse(value);
    if (!parsed.success) {
        return EMPTY_READINESS_COMPONENTS;
    }
    const publishedAt = parsed.data.published_at ?? null;
    const hasReadinessSignal = (
        parsed.data.control !== undefined
        || parsed.data.video !== undefined
        || parsed.data.hardware !== undefined
        || parsed.data.livekit_connection_state !== undefined
    );
    if (hasReadinessSignal && !isReadinessFresh(publishedAt)) {
        return staleReadinessComponents(publishedAt);
    }
    return {
        control: parsed.data.control ?? null,
        video: parsed.data.video ?? null,
        hardware: parsed.data.hardware ?? null,
        livekitConnectionState: parsed.data.livekit_connection_state ?? null,
        publishedAt,
    };
};

export const deriveAggregateReadiness = (
    readiness: ReadinessComponents
): boolean | null => {
    if (
        readiness.livekitConnectionState === null
        || readiness.control === null
        || readiness.video === null
        || readiness.hardware === null
    ) {
        return null;
    }
    return (
        readiness.livekitConnectionState === 'connected'
        && readiness.control
        && readiness.video
        && readiness.hardware
    );
};
